import { Injectable, signal } from '@angular/core';
import { OnboardingData, Document, DocumentType, FleetOperations } from './models/onboarding-data.model';
import { SupabaseClient, createClient, Session, User } from '@supabase/supabase-js';
import { initialOnboardingData } from './models/initial-onboarding-data';
import { environment } from './environments/environment';

// --- Database column types (for mapping) ---
interface DbApplication {
  id: string;
  user_id: string;
  status: string;
  current_step: string;
  decision_notes: string | null;
}

interface DbApplicationData {
  application_id: string;
  legal_entity_name: string | null;
  fleet_name: string | null;
  fleet_phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  address_city: string | null;
  address_county: string | null;
  address_postcode: string | null;
  latitude: number | null;
  longitude: number | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  use_legal_name_as_fleet_name: boolean;
  fleet_size_band: string | null;
  service_level: string | null;
  dispatch_system: string | null;
  prebook_pct: number;
  asap_pct: number;
  account_pct: number;
  card_pct: number;
  cash_pct: number;
  terms_accepted: boolean;
  terms_accepted_at: string | null;
  data_sharing_accepted: boolean;
  data_sharing_accepted_at: string | null;
  code_of_conduct_accepted: boolean;
  code_of_conduct_accepted_at: string | null;
  authorised_signatory_confirmed: boolean;
  agreements_version: string | null;
  additional_notes: string | null;
}

export interface LegalDocRecord {
  slug: string;
  version: number;
  title: string;
  html_content: string;
}

interface DbDocument {
  id: string;
  application_id: string;
  document_type: DocumentType;
  storage_path: string;
  expiry_date: string | null;
}


@Injectable({
  providedIn: 'root',
})
export class SupabaseService {
  private supabaseUrl = environment.supabaseUrl;
  private supabaseKey = environment.supabaseKey;
  
  private client: SupabaseClient;
  session = signal<Session | null>(null);
  data = signal<OnboardingData | null>(null);
  initError = signal<string | null>(null);

  constructor() {
    this.client = createClient(this.supabaseUrl, this.supabaseKey);
    this.client.auth.onAuthStateChange((event, session) => {
      this.session.set(session);
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
        this.getOnboardingData(session.user);
      } else if (event === 'SIGNED_OUT') {
        this.data.set(null);
      }
    });
  }

  async signInWithEmailOtp(email: string) {
    try {
        return await this.client.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: environment.appUrl
          }
        });
    } catch (e: any) {
        if (e.name === 'AbortError') {
            console.log('OTP sign-in was aborted.');
            return { data: null, error: null };
        }
        console.error("signInWithEmailOtp failed:", e);
        return { data: null, error: { message: e.message || 'An unexpected error occurred.' } as any };
    }
  }

  async verifyToken(email: string, token: string) {
    try {
        // Prioritize checking for existing users ('magiclink'). A returning user is a common case,
        // and this check is non-destructive for a new user's 'signup' token.
        let { data, error } = await this.client.auth.verifyOtp({ email, token, type: 'magiclink' });

        // If 'magiclink' fails, it's likely a new user with a 'signup' token.
        // This prevents the primary 'signup' token from being invalidated by a failed check.
        if (error && (error.message.includes('invalid') || error.message.includes('expired'))) {
            console.warn("Magiclink OTP verification failed, trying signup OTP type for new user.");
            const signUpAttempt = await this.client.auth.verifyOtp({ email, token, type: 'signup' });
            data = signUpAttempt.data;
            error = signUpAttempt.error;
        }
        
        if (error) console.error('Error verifying token:', error.message);
        return { data, error };
    } catch (e: any) {
        if (e.name === 'AbortError') {
            console.log('Token verification was aborted.');
            return { data: null, error: null };
        }
        console.error("verifyToken failed:", e);
        return { data: null, error: { message: e.message || 'An unexpected error occurred.' } as any };
    }
  }

  async signOut() {
    await this.client.auth.signOut();
  }
  
  async getOnboardingData(user: User) {
    this.initError.set(null); // Reset on each attempt
    try {
        // Use limit(1) with ordering instead of single() to gracefully handle
        // cases where duplicate draft rows exist for a user.
        const { data: apps, error } = await this.client
          .from('onboarding_applications')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error fetching application:', error.message);
          if (error.message.includes('does not exist') || error.message.includes('Could not find the table')) {
            this.initError.set('Database connection error: The required tables were not found. Please ensure the database schema has been set up correctly.');
          } else {
            this.initError.set(`An unexpected error occurred: ${error.message}`);
          }
        } else if (!apps || apps.length === 0) {
          await this.createNewApplication(user);
        } else {
          await this.loadApplicationData(apps[0] as DbApplication, user.email || '');
        }
    } catch (e: any) {
        if (e.name !== 'AbortError') {
            console.error('Unexpected error during initial data load:', e);
            this.initError.set(`An unexpected error occurred while loading your data: ${e.message || 'Unknown error'}`);
        } else {
            console.log('Initial data load was aborted.');
        }
    }
  }

  private async createNewApplication(user: User) {
    const defaultStep = 'contact_details';
    const { data: newApp, error: newAppError } = await this.client
      .from('onboarding_applications')
      .insert({ user_id: user.id, status: 'draft', current_step: defaultStep })
      .select()
      .single();

    if (newAppError) {
      // If a unique constraint violation occurs (another draft was just inserted
      // concurrently), fall back to loading the existing application.
      if (newAppError.code === '23505') {
        console.warn('Duplicate draft detected on insert — loading existing application instead.');
        await this.getOnboardingData(user);
        return;
      }
      console.error('Failed to create new application:', newAppError.message);
      this.initError.set('There was a problem creating your application record.');
      return;
    }

    if (!newApp) {
      console.error('Failed to create new application: no data returned');
      this.initError.set('There was a problem creating your application record.');
      return;
    }

    // Prepare the full client-side data model first
    const newClientData: OnboardingData = {
      ...initialOnboardingData,
      applicationId: newApp.id,
      email: user.email || '',
      status: newApp.status,
      currentStepKey: newApp.current_step,
      contactDetails: {
        ...initialOnboardingData.contactDetails,
        contactEmail: user.email || '',
      }
    };
    
    // Map the client data to the flat structure for the DB
    const dataForDb = this.mapDataToDb(newClientData);

    // Insert the corresponding record in the data table
    const { error: dataError } = await this.client.from('onboarding_application_data').insert({
      ...dataForDb,
      application_id: newApp.id
    });

    if (dataError) {
        console.error('Error creating initial application data record:', dataError.message);
        this.initError.set('There was a problem initializing your application data. Please try again.');
        return;
    }

    // --- Add audit log for application start ---
    const { error: logError } = await this.client.from('onboarding_application_log').insert({
        application_id: newApp.id,
        actor_user_id: user.id,
        action: 'application_started',
        description: `Application draft created by user ${user.email}.`
    });
    if (logError) {
        console.error('Failed to log application start event:', logError.message);
    }
    
    // Set the state directly, avoiding a redundant reload from the database
    this.data.set(newClientData);
  }
  
  private async loadApplicationData(app: DbApplication, email: string) {
      const appId = app.id;
      
      const handleError = (tableName: string, error: any) => {
        const errorMessage = error?.message || 'An unknown error occurred. Please check the browser console for the full error object.';
        console.error(`Error with ${tableName}:`, errorMessage, error);
        this.initError.set(`Failed to load application data from ${tableName}. The server responded: "${errorMessage}". Please contact support.`);
      };

      // --- Fetch or Create consolidated Application Data ---
      let { data: appData, error: appDataError } = await this.client.from('onboarding_application_data').select('*').eq('application_id', appId).single();
      
      if (appDataError && appDataError.code === 'PGRST116') { // No data found, create it
          console.warn(`Missing application_data for application ${appId}. Recreating.`);
          
          const initialData = this.mapDataToDb({
            ...initialOnboardingData,
            email: email,
            contactDetails: { ...initialOnboardingData.contactDetails, contactEmail: email }
          });

          const { data: newAppData, error: insertError } = await this.client.from('onboarding_application_data').insert({ ...initialData, application_id: appId }).select().single();
          if (insertError) { return handleError('onboarding_application_data', insertError); }
          appData = newAppData;
      } else if (appDataError) {
          return handleError('onboarding_application_data', appDataError);
      }

      if (this.initError()) { return; } // Stop if any repair failed

      const { data: documentsData } = await this.client.from('onboarding_documents').select('*').eq('application_id', appId);
      
      const clientData: OnboardingData = this.mapDataToClient(appData as DbApplicationData, app, email);

      if (documentsData) {
        for (const doc of documentsData as DbDocument[]) {
          const { data: urlData } = await this.client.storage.from('onboarding-documents').createSignedUrl(doc.storage_path, 3600);
          clientData.documents[doc.document_type] = {
            id: doc.id,
            storagePath: doc.storage_path,
            fileName: doc.storage_path.split('/').pop() || 'document',
            url: urlData?.signedUrl
          };
        }
      }
      this.data.set(clientData);
  }

  async saveOnboardingData(data: OnboardingData, currentStepKey?: string): Promise<boolean> {
    const { applicationId } = data;
    if (!applicationId) return false;

    const promises = [
      this.client.from('onboarding_application_data').update(this.mapDataToDb(data)).eq('application_id', applicationId),
    ];

    if (currentStepKey) {
        promises.push(
            this.client.from('onboarding_applications').update({ current_step: currentStepKey }).eq('id', applicationId)
        );
    }

    try {
        const results = await Promise.all(promises);
        const errors = results.map(r => r.error).filter(Boolean);

        if (errors.length > 0) {
          const error = errors[0]!; // Just show the first error
          console.error('Error saving progress:', error.message, errors);
           if (error.message.includes("violates row-level security policy")) {
                this.initError.set("You do not have permission to save these changes. Please check your login status and try again.");
            } else if (error.message.includes("Could not find the column")) {
                this.initError.set("A database error occurred. A required data field is missing from the database schema. Please contact support.");
            } else {
                this.initError.set(`An unexpected error occurred while saving: ${error.message}`);
            }
            return false;
        } else {
          console.log('Progress saved successfully.');
          this.initError.set(null);
          // The data object passed in is the state we want to persist.
          // We just need to update its currentStepKey if a new one was provided for navigation.
          const updatedData = currentStepKey ? { ...data, currentStepKey } : data;
          this.data.set(updatedData);
          return true;
        }
    } catch (e: any) {
        if (e.name !== 'AbortError') {
            console.error('Unexpected error during saveOnboardingData:', e);
            this.initError.set(`An unexpected error occurred while saving: ${e.message || 'Unknown error'}`);
        } else {
            console.log('Save operation was aborted.');
        }
        return false;
    }
  }
  
  async logStepProgression(applicationId: string, fromStepKey: string, toStepKey: string) {
    const user = this.session()?.user;
    if (!user) {
        console.warn('Could not log step progression: User session not found.');
        return;
    }
    
    if (fromStepKey === toStepKey) return;

    const { error: logError } = await this.client.from('onboarding_application_log').insert({
        application_id: applicationId,
        actor_user_id: user.id,
        action: 'step_progressed',
        description: `User ${user.email} moved from step '${fromStepKey}' to '${toStepKey}'.`
    });

    if (logError) {
        console.error(`Failed to log step progression event:`, logError.message);
    }
  }

  async uploadDocument(applicationId: string, file: File, docType: DocumentType, existingDocId?: string): Promise<{ document: Document | null; error: string | null; }> {
    try {
        const fileExt = file.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = `${applicationId}/${docType}/${fileName}`;

        const { error: uploadError } = await this.client.storage.from('onboarding-documents').upload(filePath, file, { upsert: true });
        if (uploadError) {
          console.error('Error uploading file:', uploadError.message);
          const friendlyMessage = uploadError.message === 'Bucket not found'
            ? 'The file storage service is not configured. Please contact support.'
            : `Upload failed: ${uploadError.message}`;
          return { document: null, error: friendlyMessage };
        }
        
        const docRecord = {
          application_id: applicationId,
          document_type: docType,
          storage_path: filePath,
        };

        const { data, error } = existingDocId 
          ? await this.client.from('onboarding_documents').update(docRecord).eq('id', existingDocId).select().single()
          : await this.client.from('onboarding_documents').insert(docRecord).select().single();
          
        if (error) {
          console.error('Error saving document record:', error.message);
          return { document: null, error: 'Could not save the document record after upload.' };
        }
        
        const { data: urlData } = await this.client.storage.from('onboarding-documents').createSignedUrl(filePath, 3600);
        const document = { id: data.id, fileName: file.name, storagePath: filePath, url: urlData?.signedUrl };
        return { document, error: null };
    } catch (e: any) {
        if (e.name === 'AbortError') {
            console.log('Document upload process was aborted.');
            return { document: null, error: null }; // Suppress error for aborts
        }
        console.error('Unhandled exception in uploadDocument:', e);
        return { document: null, error: 'An unexpected error occurred during the upload process.' };
    }
  }

  async submitApplication(applicationId: string, isResubmission: boolean) {
    try {
        const updatePayload: {
            status: string;
            submitted_at: string;
            current_step: string;
            decision?: null;
            decision_at?: null;
            decision_notes?: null;
        } = {
            status: 'submitted',
            submitted_at: new Date().toISOString(),
            current_step: 'complete'
        };

        if (isResubmission) {
            updatePayload.decision = null;
            updatePayload.decision_at = null;
            updatePayload.decision_notes = null;
        }

        const { error } = await this.client.from('onboarding_applications')
            .update(updatePayload)
            .eq('id', applicationId);
            
        if (error) {
            console.error('Error submitting application:', error.message);
            return; // Halt on error
        }

        const user = this.session()?.user;
        if (user) {
            const action = isResubmission ? 'application_resubmitted' : 'application_submitted';
            const description = isResubmission 
                ? `Application resubmitted by user ${user.email}.`
                : `Application successfully submitted by user ${user.email}.`;

            const { error: logError } = await this.client.from('onboarding_application_log').insert({
                application_id: applicationId,
                actor_user_id: user.id,
                action: action,
                description: description
            });
            if (logError) {
                console.error(`Failed to log ${action} event:`, logError.message);
            }
        } else {
            console.warn('Could not log submission event: User session not found.');
        }
    } catch (e: any) {
        if (e.name !== 'AbortError') {
            console.error('Unexpected error on submission:', e);
            this.initError.set(`An unexpected error occurred on submission: ${e.message || 'Unknown error'}`);
        } else {
            console.log('Application submission was aborted.');
        }
    }
  }

  // --- Legal Documents ---
  async getActiveLegalDocuments(): Promise<LegalDocRecord[]> {
    const { data, error } = await this.client
      .from('legal_documents')
      .select('slug, version, title, html_content')
      .eq('is_active', true);

    if (error) {
      console.error('[SupabaseService] Error fetching legal documents:', error.message);
      throw error;
    }
    return (data ?? []) as LegalDocRecord[];
  }

  // --- Mappers ---
  private mapDataToClient(dbData: DbApplicationData, app: DbApplication, email: string): OnboardingData {
    const knownDispatchSystems: Exclude<FleetOperations['dispatchSystem'], '' | 'Other'>[] = [
        'AutoCab', 'Cab9', 'CabTreasure', 'Cordic', 'iCabbi', 'Magenta', 'Sherlock'
    ];
    const dbDispatchValue = dbData.dispatch_system || '';
    
    let clientDispatchSystem: FleetOperations['dispatchSystem'] = '';
    let clientDispatchSystemOther = '';

    if (!dbDispatchValue) {
        clientDispatchSystem = '';
    } else if ((knownDispatchSystems as string[]).includes(dbDispatchValue)) {
        clientDispatchSystem = dbDispatchValue as FleetOperations['dispatchSystem'];
    } else { // It's a custom value
        clientDispatchSystem = 'Other';
        clientDispatchSystemOther = dbDispatchValue;
    }

    return {
      applicationId: app.id,
      email: email,
      status: app.status,
      rejectionReason: app.decision_notes || null,
      currentStepKey: app.current_step,
      additionalNotes: dbData.additional_notes || '',
      contactDetails: {
        legalEntityName: dbData.legal_entity_name || '',
        fleetName: dbData.fleet_name || '',
        fleetPhone: dbData.fleet_phone || '',
        address: {
          line1: dbData.address_line1 || '',
          line2: dbData.address_line2 || '',
          city: dbData.address_city || '',
          county: dbData.address_county || '',
          postcode: dbData.address_postcode || '',
          latitude: dbData.latitude ?? null,
          longitude: dbData.longitude ?? null,
        },
        contactName: dbData.contact_name || '',
        contactEmail: dbData.contact_email || '',
        contactPhone: dbData.contact_phone || '',
        useLegalNameAsFleetName: dbData.use_legal_name_as_fleet_name ?? true,
      },
      fleetOps: {
        fleetSize: (dbData.fleet_size_band || '') as OnboardingData['fleetOps']['fleetSize'],
        serviceLevel: (dbData.service_level || '') as OnboardingData['fleetOps']['serviceLevel'],
        dispatchSystem: clientDispatchSystem,
        dispatchSystemOther: clientDispatchSystemOther,
        workSplit: {
          bookingType: {
            prebook: dbData.prebook_pct ?? 50,
            asap: dbData.asap_pct ?? 50,
          },
          paymentType: {
            account: dbData.account_pct ?? 50,
            card: dbData.card_pct ?? 50,
            cash: dbData.cash_pct ?? 0,
          },
        },
      },
      confirmations: {
        terms: dbData.terms_accepted || false,
        termsAcceptedAt: dbData.terms_accepted_at || null,
        dataSharing: dbData.data_sharing_accepted || false,
        dataSharingAcceptedAt: dbData.data_sharing_accepted_at || null,
        codeOfConduct: dbData.code_of_conduct_accepted || false,
        codeOfConductAcceptedAt: dbData.code_of_conduct_accepted_at || null,
        isAuthorised: dbData.authorised_signatory_confirmed || false,
        signedVersions: this.parseSignedVersions(dbData.agreements_version),
      },
      documents: {
        EmployersLiabilityInsurance: null,
        PublicLiabilityInsurance: null,
        OperatorsLicence: null
      }
    };
  }

  private mapDataToDb(clientData: OnboardingData) {
    const { contactDetails, fleetOps, confirmations } = clientData;
    const dispatchValue = fleetOps.dispatchSystem === 'Other' 
        ? fleetOps.dispatchSystemOther 
        : fleetOps.dispatchSystem;

    return {
      legal_entity_name: contactDetails.legalEntityName || null,
      fleet_name: contactDetails.fleetName || null,
      fleet_phone: contactDetails.fleetPhone || null,
      address_line1: contactDetails.address.line1 || null,
      address_line2: contactDetails.address.line2 || null,
      address_city: contactDetails.address.city || null,
      address_county: contactDetails.address.county || null,
      address_postcode: contactDetails.address.postcode || null,
      latitude: contactDetails.address.latitude,
      longitude: contactDetails.address.longitude,
      contact_name: contactDetails.contactName || null,
      contact_email: contactDetails.contactEmail || null,
      contact_phone: contactDetails.contactPhone || null,
      use_legal_name_as_fleet_name: contactDetails.useLegalNameAsFleetName,
      fleet_size_band: fleetOps.fleetSize || null,
      service_level: fleetOps.serviceLevel || null,
      dispatch_system: dispatchValue || null,
      prebook_pct: fleetOps.workSplit.bookingType.prebook,
      asap_pct: fleetOps.workSplit.bookingType.asap,
      account_pct: fleetOps.workSplit.paymentType.account,
      card_pct: fleetOps.workSplit.paymentType.card,
      cash_pct: fleetOps.workSplit.paymentType.cash,
      terms_accepted: confirmations.terms,
      terms_accepted_at: confirmations.termsAcceptedAt,
      data_sharing_accepted: confirmations.dataSharing,
      data_sharing_accepted_at: confirmations.dataSharingAcceptedAt,
      code_of_conduct_accepted: confirmations.codeOfConduct,
      code_of_conduct_accepted_at: confirmations.codeOfConductAcceptedAt,
      authorised_signatory_confirmed: confirmations.isAuthorised,
      ...(confirmations.signedVersions ? { agreements_version: JSON.stringify(confirmations.signedVersions) } : {}),
      additional_notes: clientData.additionalNotes || null,
    };
  }

  private parseSignedVersions(raw: string | null): import('./models/onboarding-data.model').SignedVersions | null {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'object' && parsed !== null && typeof parsed.terms_of_service === 'number') {
        return parsed;
      }
    } catch { /* legacy format like 'v1' — ignore */ }
    return null;
  }
}